import { useEffect, useState } from "react"
import Icon from "@/v2-ui/icon"
import type { IconNames } from "@/v2-ui/icon/icon.types"
import Spinner from "@/v2-ui/spinner"
import cn from "@/v2-ui/utils/utils.cn"
import { useDebouncedValue } from "@/v2-ui/utils/utils.debounce"
import Button from "@/v2-backoffice-client/components/button"
import {
  runConfigurationDiagnostics,
  type LayerDiagnostic,
  type LayerDiagnosticSeverity,
} from "@/v2-backoffice-client/layer/update/diagnostics/layer.update.diagnostics.utils"

const SEVERITY_ICONS: Record<LayerDiagnosticSeverity, IconNames> = {
  ok: "check-circle",
  warning: "triangle-warning",
  error: "circle-xmark",
}

const SEVERITY_CLASSES: Record<LayerDiagnosticSeverity, string> = {
  ok: "text-green-500",
  warning: "text-amber-500",
  error: "text-destructive",
}

/** Worst severity across the results drives the collapsed pill. */
function getWorstSeverity(
  diagnostics: LayerDiagnostic[],
): LayerDiagnosticSeverity {
  if (diagnostics.some((diagnostic) => diagnostic.severity === "error")) {
    return "error"
  }
  if (diagnostics.some((diagnostic) => diagnostic.severity === "warning")) {
    return "warning"
  }
  return "ok"
}

function getPillLabel(diagnostics: LayerDiagnostic[]): string {
  const errors = diagnostics.filter((d) => d.severity === "error").length
  const warnings = diagnostics.filter((d) => d.severity === "warning").length
  if (errors > 0) return `${errors} ${errors === 1 ? "issue" : "issues"}`
  if (warnings > 0)
    return `${warnings} ${warnings === 1 ? "warning" : "warnings"}`
  return "Checks pass"
}

/**
 * The work order the "Fix with AI" button hands to the assistant: the exact
 * findings, plus what a correct fix looks like — so the model repairs the
 * draft instead of redesigning it.
 */
function buildFixPrompt(diagnostics: LayerDiagnostic[]): string {
  const issues = diagnostics.filter(
    (diagnostic) => diagnostic.severity !== "ok",
  )
  const lines = issues.map(
    (issue, index) => `${index + 1}. [${issue.severity}] ${issue.message}`,
  )
  return `The live diagnostics found ${issues.length} ${
    issues.length === 1 ? "issue" : "issues"
  } in the current Configuration draft:

${lines.join("\n")}

Fix exactly these issues in the draft. Cross-check every referenced field name against the live service metadata and replace wrong references with the closest actually-existing field (or drop the entry when the service has no equivalent — say so if you do). Change nothing unrelated, and return the full corrected Configuration as a suggestion.`
}

export type LayerUpdateDiagnosticsProps = {
  className?: string
  /** Current editor draft (not necessarily saved). */
  draft: string
  /**
   * Runs the service-backed checks. Only the Configuration blob carries a
   * service `url` and field references to cross-check; the other documents
   * mount this purely to surface `extra`.
   */
  withServiceChecks?: boolean
  /**
   * Findings the host already knows about — drift against the accepted
   * baseline — merged into the same pill. Drift is a fact about this
   * document, so it belongs where its other problems are reported rather
   * than in a tab the operator has to remember to open.
   */
  extra?: LayerDiagnostic[]
  /**
   * Renders "Fix with AI" in the expanded findings panel; called with a
   * concrete repair prompt for the assistant (the host opens it and sends).
   */
  onFixWithAi?(prompt: string): void
}

/**
 * Live semantic checks for the draft: pings the service `url` and
 * cross-checks referenced field names against the service's actual
 * fields, alongside any findings the host supplies. Purely advisory —
 * results never block saving. Rendered as a floating pill in the editor's
 * corner (via the Editor `overlay` slot); clicking it expands the full
 * list of findings.
 */
function LayerUpdateDiagnostics(props: LayerUpdateDiagnosticsProps) {
  const {
    draft,
    withServiceChecks = true,
    extra,
    onFixWithAi,
    className,
  } = props

  const [serviceDiagnostics, setServiceDiagnostics] = useState<
    LayerDiagnostic[]
  >([])
  const [isChecking, setIsChecking] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  // Debounce so we only hit the service once the user pauses typing.
  const debouncedDraft = useDebouncedValue(draft, 800)

  useEffect(() => {
    if (!withServiceChecks) {
      setServiceDiagnostics([])
      setIsChecking(false)
      return
    }

    let cancelled = false
    setIsChecking(true)

    runConfigurationDiagnostics(debouncedDraft)
      .then((result) => {
        if (!cancelled) setServiceDiagnostics(result)
      })
      .finally(() => {
        if (!cancelled) setIsChecking(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedDraft, withServiceChecks])

  // Drift first: it describes a change the layer hasn't been adapted to,
  // which outranks a style nit found in the draft.
  const diagnostics = [...(extra ?? []), ...serviceDiagnostics]

  if (!isChecking && diagnostics.length === 0) return null

  const worst = getWorstSeverity(diagnostics)

  return (
    <div
      className={cn(
        "layer-update-diagnostics absolute bottom-2 right-3 z-10 flex flex-col items-end gap-1.5",
        className,
      )}
    >
      {isExpanded && diagnostics.length > 0 && (
        <div className="flex max-w-md flex-col gap-1.5 rounded-md border bg-background/95 p-2.5 text-xs shadow-lg">
          <div className="flex max-h-48 flex-col gap-1.5 overflow-y-auto">
            {diagnostics.map((diagnostic, index) => (
              <p
                key={index}
                className={cn(
                  "flex items-center gap-1.5 leading-none",
                  SEVERITY_CLASSES[diagnostic.severity],
                )}
              >
                {/* leading-none inside the icon too: the glyph inherits the
                    text line-height otherwise and rides ~2px below center. */}
                <Icon
                  src={SEVERITY_ICONS[diagnostic.severity]}
                  size={12}
                  className="shrink-0 leading-none"
                />
                {diagnostic.message}
              </p>
            ))}
          </div>

          {onFixWithAi && worst !== "ok" && (
            <Button
              size="xs"
              iconSrc="sparkles"
              className="self-end"
              title="Open the AI assistant with a repair prompt for exactly these findings"
              data-feature-code="UI_BackofficeLayers_Diagnostics_FixWithAi"
              onClick={() => {
                onFixWithAi(buildFixPrompt(diagnostics))
                setIsExpanded(false)
              }}
            >
              Fix with AI
            </Button>
          )}
        </div>
      )}

      <button
        type="button"
        title={
          isExpanded ? "Hide the diagnostics" : "Show what the checks found"
        }
        aria-expanded={isExpanded}
        className={cn(
          "flex items-center gap-1.5 rounded-full border bg-background/90 px-2.5 py-1 text-[11px] leading-none shadow-md transition-colors hover:bg-background",
          !isChecking && SEVERITY_CLASSES[worst],
        )}
        data-feature-code="UI_BackofficeLayers_Diagnostics_Toggle"
        onClick={() => setIsExpanded((open) => !open)}
      >
        {isChecking ? (
          <>
            {/* Empty children on purpose: Spinner's default label is the
                Norwegian "Laster..", which would sit next to this one. */}
            <Spinner displaySize="xs" className="relative">
              {null}
            </Spinner>
            <span className="text-muted-foreground">Checking…</span>
          </>
        ) : (
          <>
            <Icon
              src={SEVERITY_ICONS[worst]}
              size={12}
              className="leading-none"
            />
            {getPillLabel(diagnostics)}
          </>
        )}
      </button>
    </div>
  )
}

export default LayerUpdateDiagnostics
